import React, { useState, useRef, useEffect } from 'react';
import { AlertCircle, Copy, Edit2, Save, X, RotateCcw, HelpCircle } from 'lucide-react';
import { SCORE_RANGE } from '../../config/constants';
import { getDomainGuidelines } from '../../config/domainGuidelines';

const AssessmentItem = ({ 
  item, 
  category, 
  isEditing, 
  editedScore, 
  onStartEdit, 
  onSaveEdit, 
  onCancelEdit, 
  onRevert, 
  onCopy,
  onScoreChange 
}) => {
  const displayScore = item.userScore || item.score;
  const isEdited = item.userScore !== null && item.userScore !== undefined;
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const hideTimeoutRef = useRef(null);
  const tooltipContainerRef = useRef(null);
  const buttonRef = useRef(null);
  
  const guidelines = getDomainGuidelines(item.id);

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tooltipContainerRef.current && 
        !tooltipContainerRef.current.contains(event.target)
      ) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTooltip]);

  // Calculate tooltip position to prevent overflow
  const calculateTooltipPosition = () => {
    if (!buttonRef.current) return { isMobile: false };

    const viewportWidth = window.innerWidth;
    
    // On mobile, use fixed centered positioning
    if (viewportWidth < 640) {
      return { isMobile: true };
    }

    // Desktop: calculate relative position
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const tooltipWidth = 500; // Max width of tooltip
    const tooltipMaxHeight = window.innerHeight * 0.7; // 70vh
    const viewportHeight = window.innerHeight;
    const margin = 16; // 1rem margin

    let style = { isMobile: false };

    // Horizontal positioning - Check if tooltip would overflow on the right
    const spaceOnRight = viewportWidth - buttonRect.left;

    if (spaceOnRight < tooltipWidth + margin) {
      // Not enough space on right, align to right edge of viewport
      style.right = `${margin}px`;
    } else if (buttonRect.left < margin) {
      // Too close to left edge, align to left edge of viewport
      style.left = `${margin}px`;
    } else {
      // Enough space, position naturally from the icon
      style.left = '0';
    }

    // Vertical positioning - Check if tooltip would overflow below viewport
    const headerHeight = 100; // Approximate height of sticky header with "Copy All Results"
    const spaceBelow = viewportHeight - buttonRect.bottom - 8; // 8px is top-8 gap
    const spaceAbove = buttonRect.top - headerHeight; // Space above minus header

    if (spaceBelow < tooltipMaxHeight + margin) {
      // Not enough space below, check if there's more space above
      if (spaceAbove > spaceBelow && spaceAbove > 150) {
        // Position above the icon (but below header)
        style.bottom = '100%';
        style.marginBottom = '8px';
        style.top = 'auto';
        style.maxHeight = `${Math.min(spaceAbove - margin, tooltipMaxHeight)}px`;
      } else {
        // Keep below but adjust to fit viewport
        style.top = '32px'; // 8 * 4px
        style.maxHeight = `${spaceBelow - margin}px`;
      }
    } else {
      // Default position below icon with standard max height
      style.top = '32px'; // 8 * 4px (top-8)
      style.maxHeight = '70vh';
    }

    return style;
  };

  const handleMouseEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    // Calculate position before showing
    setTooltipStyle(calculateTooltipPosition());
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    // Add a small delay before hiding to allow mouse movement between icon and tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 150); // 150ms delay
  };

  const handleClick = (e) => {
    e.preventDefault();
    // Toggle tooltip on click (useful for mobile)
    if (showTooltip) {
      setShowTooltip(false);
    } else {
      // Calculate position before showing
      setTooltipStyle(calculateTooltipPosition());
      setShowTooltip(true);
    }
  };

  return (
    <div className={`border rounded-lg p-5 mb-4 ${isEdited ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'} ${item.flaggedForReview ? 'border-amber-400' : ''}`}>
      {item.flaggedForReview && (
        <div className="bg-amber-100 border-l-4 border-amber-500 p-3 mb-3 flex items-start">
          <AlertCircle className="text-amber-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-amber-800 text-sm">Insufficient Information</p>
            <p className="text-amber-700 text-sm">Professional review required</p>
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-gray-900 text-lg">{item.name}</h4>
            {guidelines && (
              <div 
                ref={tooltipContainerRef}
                className="relative" 
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  ref={buttonRef}
                  onClick={handleClick}
                  className={`transition-colors ${showTooltip ? 'text-teal-700' : 'text-teal-600 hover:text-teal-700'}`}
                  title="View scoring guidelines"
                >
                  <HelpCircle size={20} />
                </button>
                {showTooltip && (
                  <div 
                    className={`z-50 w-[500px] max-w-[calc(100vw-2rem)] bg-white border-2 border-teal-600 rounded-lg shadow-2xl p-4 overflow-y-auto ${
                      tooltipStyle.isMobile 
                        ? 'fixed left-1/2 -translate-x-1/2 top-20 max-h-[70vh]' 
                        : 'absolute'
                    }`}
                    style={tooltipStyle.isMobile ? {} : tooltipStyle}
                  >
                    <h5 className="font-bold text-teal-700 mb-3 text-base border-b border-teal-200 pb-2">
                      {guidelines.title} - Scoring Guidelines
                    </h5>
                    <div className="space-y-3">
                      {[7, 6, 5, 4, 3, 2, 1].map((level) => (
                        <div key={level} className={`p-2 rounded ${displayScore === level ? 'bg-teal-50 border-l-4 border-teal-600' : 'bg-gray-50'}`}>
                          <div className="flex items-start gap-2">
                            <span className={`font-bold min-w-[60px] flex-shrink-0 ${displayScore === level ? 'text-teal-700' : 'text-gray-700'}`}>
                              Level {level}:
                            </span>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {guidelines.levels[level]}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {isEdited && (
            <span className="inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded mt-1">
              Edited
            </span>
          )}
        </div>
        <button
          onClick={() => onCopy(item)}
          className="text-gray-400 hover:text-gray-600 p-1"
          title="Copy item"
        >
          <Copy size={18} />
        </button>
      </div>

      <div className="mb-3">
        {isEditing ? (
          <div className="flex items-center gap-3">
            <select
              value={editedScore}
              onChange={(e) => onScoreChange(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-2xl font-bold w-24"
            >
              <option value="">—</option>
              {SCORE_RANGE.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500">1-7 scale</span>
            {item.score && (
              <span className="text-sm text-gray-600">(AI suggested: {item.score})</span>
            )}
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => onSaveEdit(category, item.id)}
                className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 flex items-center gap-1"
              >
                <Save size={16} /> Save
              </button>
              <button
                onClick={onCancelEdit}
                className="bg-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-400 flex items-center gap-1"
              >
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-4xl font-bold text-gray-900">
              {displayScore || '—'}
            </span>
            <span className="text-sm text-gray-500">1-7 scale</span>
            <button
              onClick={() => onStartEdit(category, item.id, displayScore)}
              className="ml-auto text-blue-600 hover:text-blue-800 flex items-center gap-1 px-3 py-1 border border-blue-600 rounded hover:bg-blue-50"
            >
              <Edit2 size={16} /> Edit
            </button>
          </div>
        )}
      </div>

      <div className="bg-gray-50 p-3 rounded border border-gray-200">
        <p className="text-sm font-semibold text-gray-700 mb-1">AI Reasoning:</p>
        <p className="text-sm text-gray-600">{item.justification}</p>
      </div>

      {isEdited && (
        <button
          onClick={() => onRevert(category, item.id)}
          className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          <RotateCcw size={14} /> Revert to AI suggestion
        </button>
      )}
    </div>
  );
};

export default AssessmentItem;

